import { zonedStamp } from '../../../inputs/lt-utc.js';
import { toCsv } from '../../voyage/all-reports.js';
import type {
  RaComplianceReportRow,
  RaLibraryReportRow,
  RaVerificationReportRow,
} from './types.js';

/**
 * Monitoring & Reports'un CSV'leri (R24; ADR 0095 §2): ekrandaki süzülmüş satırlar, ekrandaki
 * sütunlarla. Tarihler `YYYY-MM-DD`, anlar UTC ve gemi saati farkıyla (R26). Hücreler CSV
 * enjeksiyonuna karşı kaçışlıdır (`toCsv`).
 */

type T = (key: string, options?: Record<string, unknown>) => string;

const or = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '' : String(value);
const utc = (iso: string | null | undefined) => (iso ? `${zonedStamp(iso)} UTC` : '');
const offset = (minutes: number | null | undefined) => {
  if (minutes === null || minutes === undefined) return '';
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

export function libraryCsv(rows: readonly RaLibraryReportRow[], t: T): string {
  const c = (key: string) => t(`riskDashboard.reports.columns.${key}`);
  return toCsv(
    [
      c('code'),
      c('title'),
      c('status'),
      c('workingRevision'),
      c('publishedRevision'),
      c('publishedAt'),
      c('matrix'),
      c('rows'),
      c('residual'),
      c('acceptance'),
      c('reviewDay'),
      c('review'),
      c('assignedTo'),
    ],
    rows.map((r) => [
      r.code,
      r.title,
      t(`riskLibrary.statuses.${r.status}`),
      or(r.workingRevision),
      or(r.publishedRevision),
      utc(r.publishedAt),
      or(r.matrixVersion),
      String(r.rows),
      r.residual ? `${t(`risk.levels.${r.residual.level}`)} (${r.residual.score})` : '',
      r.acceptance ? t(`riskDashboard.acceptance.${r.acceptance}`) : '',
      or(r.reviewDay),
      r.review ? t(`riskDashboard.review.${r.review}`) : '',
      r.assignedTo.join('; '),
    ]),
  );
}

export function complianceCsv(rows: readonly RaComplianceReportRow[], t: T): string {
  const c = (key: string) => t(`riskDashboard.reports.columns.${key}`);
  return toCsv(
    [
      c('vessel'),
      c('code'),
      c('title'),
      c('revision'),
      c('publishedAt'),
      c('acknowledgement'),
      c('late'),
      c('acknowledgedBy'),
      c('acknowledgedRole'),
      c('acknowledgedAt'),
      c('shipOffset'),
      c('reviewDay'),
      c('review'),
      c('assessments'),
    ],
    rows.map((r) => [
      r.vesselName,
      r.code,
      r.title,
      r.revision,
      utc(r.publishedAt),
      t(`riskRegister.acknowledgement.${r.acknowledgement}`),
      r.late ? t('riskDashboard.yes') : '',
      or(r.acknowledgedBy),
      or(r.acknowledgedRole),
      utc(r.acknowledgedAt),
      offset(r.acknowledgedTzOffsetMin),
      or(r.reviewDay),
      r.review ? t(`riskDashboard.review.${r.review}`) : '',
      String(r.assessments),
    ]),
  );
}

export function verificationCsv(rows: readonly RaVerificationReportRow[], t: T): string {
  const c = (key: string) => t(`riskDashboard.reports.columns.${key}`);
  return toCsv(
    [
      c('vessel'),
      c('number'),
      c('operation'),
      c('location'),
      c('code'),
      c('revision'),
      c('plannedStart'),
      c('shipOffset'),
      c('status'),
      c('round'),
      c('answered'),
      c('failed'),
      c('residual'),
      c('authorizedBy'),
      c('validUntil'),
      c('stops'),
      c('lastStop'),
      c('createdBy'),
      c('createdAt'),
    ],
    rows.map((r) => [
      r.vesselName,
      r.number,
      r.operation,
      or(r.location),
      r.code,
      r.revision,
      utc(r.plannedStart),
      offset(r.tzOffsetMin),
      t(`riskVerification.statuses.${r.status}`),
      String(r.round),
      `${r.answered}/${r.total}`,
      String(r.failed),
      r.risk.residual
        ? `${t(`risk.levels.${r.risk.residual.level}`)} (${r.risk.residual.score})`
        : '',
      or(r.authorizedByName),
      utc(r.validUntil),
      String(r.stops ?? 0),
      r.lastStop ? `${utc(r.lastStop.at)} ${or(r.lastStop.reason)}`.trim() : '',
      or(r.createdByName),
      utc(r.createdAt),
    ]),
  );
}

/** Raporun süzgeci: metin (kod, başlık, gemi, operasyon) ve durum. */
export function matches(query: string, ...fields: (string | null | undefined)[]): boolean {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return true;
  return fields.some((f) => (f ?? '').toLocaleLowerCase().includes(q));
}
